import React, { useState, useEffect } from "react";
import { adminGetAllContacts } from "../api/admin";
import { toast } from "react-toastify";
import ContactDetailsView from "./ContactDetailsView";
import "./ContactManagement.css";
import "./UserManagement.css"; // Reuse existing table and layout styles

const ContactManagement = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState(null);

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await adminGetAllContacts();
            if (response.data) {
                // The response might be an array directly or inside a success object
                const allContacts = Array.isArray(response.data) ? response.data : (response.data.data || []);
                setContacts(allContacts);
            } else {
                toast.error("Failed to fetch contacts");
            }
        } catch (error) {
            console.error("Error fetching contacts:", error);
            toast.error("Error fetching contacts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const filtered = contacts.filter(contact => {
            const term = searchTerm.toLowerCase();
            return (
                (contact.name?.toLowerCase() || "").includes(term) ||
                (contact.email?.toLowerCase() || "").includes(term) ||
                (contact.message?.toLowerCase() || "").includes(term)
            );
        });
        setFilteredContacts(filtered);
    }, [searchTerm, contacts]);

    // Scroll to top when switching between views
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedContactId]);

    const handleDeleted = () => {
        setSelectedContactId(null);
        fetchContacts();
    };

    const handleDeleteClick = async (id) => {
        if (!id) {
            toast.error("Cannot delete: Missing ID");
            return;
        }
        if (window.confirm("Delete this contact submission?")) {
            try {
                const { adminDeleteContact } = await import("../api/admin");
                const response = await adminDeleteContact(id);
                if (response.status === 200) {
                    toast.success("Deleted successfully");
                    fetchContacts();
                }
            } catch (error) {
                toast.error("Error deleting contact");
            }
        }
    };

    return (
        <div className="user-management-container">
            {selectedContactId === null ? (
                <>
                    <div className="page-header">
                        <h2 className="adminproducts">Contact Submissions</h2>
                        <div className="header-actions">
                            <div className="search-container">
                                <span className="search-label">Search:</span>
                                <input
                                    type="text"
                                    className="user-search-input"
                                    placeholder="Search by name, email, message..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="header-controls-row">
                                <div className="admin-count-badge">
                                    Total : {filteredContacts.length}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="users-table-wrapper">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#7e8299' }}>Loading submissions...</div>
                        ) : (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Message Snippet</th>
                                        <th>Date</th>
                                        <th style={{ textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredContacts.length > 0 ? (
                                        filteredContacts.map((contact, index) => (
                                            <tr key={contact.id || contact._id || index}>
                                                <td className="user-name-cell">{contact.name || "N/A"}</td>
                                                <td>{contact.email || "-"}</td>
                                                <td className="message-snippet-cell">
                                                    {contact.message ? (contact.message.length > 60 ? contact.message.substring(0, 60) + "..." : contact.message) : "-"}
                                                </td>
                                                <td>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : "N/A"}</td>
                                                <td>
                                                    <div className="action-buttons" style={{ justifyContent: 'center', gap: '10px' }}>
                                                        <button 
                                                            className="btn-edit" 
                                                            onClick={() => {
                                                                const id = contact.id || contact._id || contact.contact_id;
                                                                if (id) {
                                                                    setSelectedContactId(id);
                                                                } else {
                                                                    toast.warning("Contact ID not found in data");
                                                                    console.warn("Contact missing ID:", contact);
                                                                }
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                        <button 
                                                            className="btn-delete" 
                                                            onClick={() => handleDeleteClick(contact.id || contact._id || contact.contact_id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="empty-table-cell">
                                                <div className="no-data-alert">
                                                    <span className="no-data-icon">✉️</span>
                                                    <p>No contact submissions found.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="page-header">
                        <div className="header-with-back">
                            <button className="back-btn" onClick={() => setSelectedContactId(null)}>← Back to List</button>
                            <h2 className="adminproducts">Submission Details</h2>
                        </div>
                    </div>
                    <ContactDetailsView contactId={selectedContactId} onDeleted={handleDeleted} />
                </>
            )}
        </div>
    );
};

export default ContactManagement;
